import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { brandService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { Spinner } from '../../components/Spinner';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

export const BrandModels = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [showAddBrand, setShowAddBrand]           = useState(false);
  const [newBrandName, setNewBrandName]           = useState('');
  const [editingBrandId, setEditingBrandId]       = useState(null);
  const [editingBrandName, setEditingBrandName]   = useState('');
  const [addingModelFor, setAddingModelFor]       = useState(null);
  const [newModelName, setNewModelName]           = useState('');

  const { data, isLoading } = useQuery('brands', brandService.getBrands, {
    staleTime: 60_000,
  });

  // Backend returns { success, data: { brands } } → axios wraps as response.data
  const brands = data?.data?.data?.brands || [];

  // ── Mutations ─────────────────────────────────────────────────────
  const createBrandMutation = useMutation(brandService.createBrand, {
    onSuccess: () => {
      toast.success('Brand created');
      queryClient.invalidateQueries('brands');
      setNewBrandName('');
      setShowAddBrand(false);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to create brand'),
  });

  const updateBrandMutation = useMutation(
    ({ id, data }) => brandService.updateBrand(id, data),
    {
      onSuccess: () => {
        toast.success('Brand updated');
        queryClient.invalidateQueries('brands');
        setEditingBrandId(null);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to update brand'),
    }
  );

  const deleteBrandMutation = useMutation(brandService.deleteBrand, {
    onSuccess: () => {
      toast.success('Brand deleted');
      queryClient.invalidateQueries('brands');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete brand'),
  });

  const addModelMutation = useMutation(
    ({ brandId, data }) => brandService.addModel(brandId, data),
    {
      onSuccess: () => {
        toast.success('Model added');
        queryClient.invalidateQueries('brands');
        setNewModelName('');
        setAddingModelFor(null);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to add model'),
    }
  );

  const deleteModelMutation = useMutation(
    ({ brandId, modelId }) => brandService.deleteModel(brandId, modelId),
    {
      onSuccess: () => {
        toast.success('Model removed');
        queryClient.invalidateQueries('brands');
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to remove model'),
    }
  );

  // Guard after all hooks
  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return (
      <div className="text-center py-16">
        <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h2 className="mt-2 text-lg font-medium text-gray-900">Access Denied</h2>
        <p className="mt-1 text-sm text-gray-500">Only admins can manage brands and models.</p>
      </div>
    );
  }

  // ── Handlers ─────────────────────────────────────────────────────
  const handleCreateBrand = () => {
    if (!newBrandName.trim()) { toast.error('Brand name is required'); return; }
    createBrandMutation.mutate({ name: newBrandName.trim() });
  };

  const handleSaveBrandEdit = (id) => {
    if (!editingBrandName.trim()) { toast.error('Brand name is required'); return; }
    updateBrandMutation.mutate({ id, data: { name: editingBrandName.trim() } });
  };

  const handleAddModel = (brandId) => {
    if (!newModelName.trim()) { toast.error('Model name is required'); return; }
    addModelMutation.mutate({ brandId, data: { name: newModelName.trim() } });
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Brands &amp; Models</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage vehicle brands and their models used when creating leads.
          </p>
        </div>
        <button onClick={() => setShowAddBrand(true)} className="btn-primary">
          <PlusIcon className="h-4 w-4 mr-1.5" />
          Add Brand
        </button>
      </div>

      {/* Add brand form */}
      {showAddBrand && (
        <div className="bg-white shadow rounded-lg p-4 mb-4 flex items-center gap-3">
          <input
            type="text"
            value={newBrandName}
            onChange={e => setNewBrandName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreateBrand()}
            placeholder="Brand name (e.g. Tata, Maruti)"
            className="input flex-1 text-sm"
            autoFocus
          />
          <button
            onClick={handleCreateBrand}
            disabled={createBrandMutation.isLoading}
            className="btn-primary py-2"
          >
            {createBrandMutation.isLoading ? <Spinner size="sm" /> : 'Create'}
          </button>
          <button
            onClick={() => { setShowAddBrand(false); setNewBrandName(''); }}
            className="btn-outline-secondary py-2"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Brand list */}
      {isLoading ? (
        <div className="p-12 text-center"><Spinner size="lg" /></div>
      ) : (
        <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
          {brands.map(brand => {
            const activeModels = brand.models?.filter(m => m.isActive) || [];

            return (
              <div key={brand._id} className="p-4">
                {/* Brand name row */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {editingBrandId === brand._id ? (
                      <input
                        type="text"
                        value={editingBrandName}
                        onChange={e => setEditingBrandName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSaveBrandEdit(brand._id)}
                        className="input text-sm flex-1 max-w-xs"
                        autoFocus
                      />
                    ) : (
                      <span className="font-semibold text-gray-900">{brand.name}</span>
                    )}
                  </div>

                  <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                    {editingBrandId === brand._id ? (
                      <>
                        <button
                          onClick={() => handleSaveBrandEdit(brand._id)}
                          disabled={updateBrandMutation.isLoading}
                          className="p-1.5 text-green-600 hover:text-green-700"
                          title="Save"
                        >
                          <CheckIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingBrandId(null)}
                          className="p-1.5 text-gray-400 hover:text-gray-600"
                          title="Cancel"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => { setEditingBrandId(brand._id); setEditingBrandName(brand.name); }}
                          className="p-1.5 text-gray-400 hover:text-primary-600"
                          title="Rename brand"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete brand "${brand.name}" and all its models?`)) {
                              deleteBrandMutation.mutate(brand._id);
                            }
                          }}
                          disabled={deleteBrandMutation.isLoading}
                          className="p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-40"
                          title="Delete brand"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Models */}
                <div className="ml-1">
                  <div className="flex flex-wrap gap-2 mb-2">
                    {activeModels.map(m => (
                      <span
                        key={m._id}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-100"
                      >
                        {m.name}
                        <button
                          onClick={() => {
                            if (window.confirm(`Remove model "${m.name}"?`)) {
                              deleteModelMutation.mutate({ brandId: brand._id, modelId: m._id });
                            }
                          }}
                          className="text-blue-400 hover:text-red-500 ml-0.5"
                          title="Remove model"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    {activeModels.length === 0 && (
                      <span className="text-xs text-gray-400 italic">No models yet — add one below</span>
                    )}
                  </div>

                  {/* Add model inline */}
                  {addingModelFor === brand._id ? (
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="text"
                        value={newModelName}
                        onChange={e => setNewModelName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAddModel(brand._id)}
                        placeholder="Model name (e.g. Nexon)"
                        className="input text-xs flex-1 max-w-xs"
                        autoFocus
                      />
                      <button
                        onClick={() => handleAddModel(brand._id)}
                        disabled={addModelMutation.isLoading}
                        className="btn-primary py-1.5 text-xs"
                      >
                        {addModelMutation.isLoading ? <Spinner size="sm" /> : 'Add'}
                      </button>
                      <button
                        onClick={() => { setAddingModelFor(null); setNewModelName(''); }}
                        className="btn-outline-secondary py-1.5 text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setAddingModelFor(brand._id); setNewModelName(''); }}
                      className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 mt-1"
                    >
                      <PlusIcon className="h-3.5 w-3.5" />
                      Add Model
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {brands.length === 0 && (
            <div className="p-12 text-center text-sm text-gray-400">
              No brands yet. Click "Add Brand" to create one.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
